import { supabase } from '../supabase/client';
import { getAuthenticatedClient } from '../supabase/authenticatedClient';

export const getProjects = async () => {
  const { data, error } = await supabase
    .from('projects')
    .select(
      `
      *,
      project_contacts (
        id,
        role,
        contact:contacts (
          id,
          first_name,
          last_name,
          email,
          phone,
          company
        )
      ),
      project_comments (
        id,
        comment,
        created_at,
        user_id
      )
    `
    )
    .order('title');

  if (error) {
    throw error;
  }

  return data;
};

export const createProject = async ({ contact_ids, ...projectData }) => {
  const client = await getAuthenticatedClient();

  // Get current destination/city
  const { data: cityData, error: cityError } = await client
    .from('cities')
    .select('id')
    .limit(1)
    .single();

  if (cityError) {
    throw cityError;
  }

  // Create the project
  const { data: project, error: projectError } = await client
    .from('projects')
    .insert([
      {
        ...projectData,
        city_id: cityData.id,
      },
    ])
    .select()
    .single();

  if (projectError) {
    throw projectError;
  }

  // Create project contacts
  if (contact_ids && contact_ids.length > 0) {
    const { error: contactsError } = await client.from('project_contacts').insert(
      contact_ids.map(contactId => ({
        project_id: project.id,
        contact_id: contactId,
        role: 'member',
      }))
    );

    if (contactsError) {
      throw contactsError;
    }
  }

  return project;
};

export const updateProject = async (id, { contact_ids, ...projectData }) => {
  const client = await getAuthenticatedClient();

  // Get current destination/city if not already set
  if (!projectData.city_id) {
    const { data: cityData, error: cityError } = await client
      .from('cities')
      .select('id')
      .limit(1)
      .single();

    if (cityError) {
      throw cityError;
    }

    projectData.city_id = cityData.id;
  }

  // Update the project
  const { data: project, error: projectError } = await client
    .from('projects')
    .update(projectData)
    .eq('id', id)
    .select()
    .single();

  if (projectError) {
    throw projectError;
  }

  // Delete existing project contacts
  const { error: deleteError } = await client
    .from('project_contacts')
    .delete()
    .eq('project_id', id);

  if (deleteError) {
    throw deleteError;
  }

  // Create new project contacts
  if (contact_ids && contact_ids.length > 0) {
    const { error: contactsError } = await client.from('project_contacts').insert(
      contact_ids.map(contactId => ({
        project_id: id,
        contact_id: contactId,
        role: 'member',
      }))
    );

    if (contactsError) {
      throw contactsError;
    }
  }

  return project;
};

export const deleteProject = async id => {
  const client = await getAuthenticatedClient();
  const { error } = await client.from('projects').delete().eq('id', id);

  if (error) {
    throw error;
  }
};

export const addProjectComment = async (projectId, comment) => {
  const client = await getAuthenticatedClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data, error } = await client
    .from('project_comments')
    .insert([
      {
        project_id: projectId,
        user_id: user.id,
        comment,
      },
    ])
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
};
